// VOID: Card Warfare — Commander Call-Sign Selection Modal
//
// Modal dialog for setting/editing the player's unique Call-Sign.
// Follows the modern VOID Conflict glass aesthetic, reusing central menu
// components (fullscreen-card-panel, status-pill, live avatar dossier, start-btn).

import { getAuth, getDisplayName, getMMR, setDisplayName } from '../net/playerIdentity.js';
import { hasAlphaAccess, checkAlphaAccess } from './alphaGate.js';
import { showRecoveryCode } from './accountOverlay.js';
import { getRankTier } from './leaderboardModal.js';
import { getCardThumbUrl } from '../data/cardThumbUrl.js';
import { getCardImageUrl, CARDS_DATA } from '../data/cards.js';
import { inventory } from '../engine/inventory.js';
import { progress } from '../engine/progress.js';
import { resolveAvatarImageUrl } from './avatarImage.js';

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getActiveAvatarUrl() {
  try {
    const equippedArtwork = inventory?.getEquippedProfileArtwork?.();
    if (equippedArtwork && Array.isArray(CARDS_DATA)) {
      const card = CARDS_DATA.find((c) => c.name === equippedArtwork);
      if (card?.images?.[0]) {
        return getCardThumbUrl(card.images[0], 'm');
      }
    }
    const equippedEntry = progress?.getEquippedAvatar?.();
    if (equippedEntry) {
      const resolved = resolveAvatarImageUrl(equippedEntry);
      if (resolved) return resolved;
    }
  } catch (err) {
    // Fallback gracefully
  }
  return getCardThumbUrl('1_Hypermarine.png', 'm');
}

export function showCallsignModal(opts = {}) {
  if (!hasAlphaAccess()) {
    checkAlphaAccess({ prompt: true }).then((granted) => {
      if (granted) showCallsignModal(opts);
    });
    return;
  }

  const onSaved = typeof opts === 'function' ? opts : opts?.onSaved;
  const game = opts?.game || (typeof window !== 'undefined' ? window.game : null);
  const audio = game?.audio;

  const existing = document.querySelector('.callsign-overlay');
  if (existing) {
    existing.remove();
    game?.overlayGate?.release?.();
  }
  game?.overlayGate?.acquire?.();

  const auth = getAuth();
  const hasAccount = !!(auth && auth.accountId);
  const current = getDisplayName();
  const initialValue = current && current !== 'Commander' ? current : '';
  const mmr = getMMR();
  const tier = getRankTier(mmr);
  const avatarUrl = getActiveAvatarUrl();

  const layer = document.createElement('div');
  layer.className = 'callsign-overlay account-overlay';

  let closed = false;
  const closeModal = () => {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', handleEsc);
    layer.remove();
    game?.overlayGate?.release?.();
  };

  const currentDisplayEscaped = escapeHtml(initialValue || 'COMMANDER');

  layer.innerHTML = `
    <div class="callsign-panel fullscreen-card-panel">
      <!-- Top Title & Navigation Bar -->
      <header class="callsign-header">
        <div class="callsign-header-left">
          <div class="fullscreen-dialog-category">OPERATIVE DOSSIER · IDENTITY</div>
          <h2 class="fullscreen-dialog-title callsign-title">COMMANDER CALL-SIGN</h2>
        </div>
        <div class="callsign-header-right">
          <div class="fullscreen-dialog-status-pill status-pill-circular" title="Central Command Telemetry">
            <span class="status-indicator-dot"></span>
          </div>
          <button type="button" class="callsign-close-btn" id="callsign-close-btn" title="Close (Esc)" aria-label="Close">✕</button>
        </div>
      </header>

      <!-- Live Dossier Identity Preview -->
      <div class="callsign-dossier-preview">
        <div class="callsign-avatar-frame">
          <img src="${avatarUrl}" alt="Commander Avatar" class="callsign-avatar-img" />
        </div>
        <div class="callsign-dossier-info">
          <div class="callsign-live-name ${!initialValue ? 'callsign-placeholder-name' : ''}" id="callsign-preview-name">
            ${currentDisplayEscaped}
          </div>
          <div class="callsign-dossier-meta">
            <span class="callsign-rank-badge" style="color: ${tier.color}; background: ${tier.color}15; border-color: ${tier.color}40;">
              ${tier.icon} ${tier.name} · ${mmr} MMR
            </span>
            <span class="callsign-operative-id">${hasAccount ? `#${auth.accountId}` : 'GUEST · UNLINKED'}</span>
          </div>
        </div>
      </div>

      <p class="callsign-desc">
        Set your unique Commander Call-Sign for the Ranked Arena, HUD, and Global Leaderboard.
      </p>

      <div class="callsign-input-group">
        <div class="callsign-input-header">
          <label for="callsign-input-field" class="callsign-input-label">CALL-SIGN DESIGNATION</label>
          <span id="callsign-char-count" class="callsign-char-count">${initialValue.length} / 20</span>
        </div>
        <input
          type="text"
          id="callsign-input-field"
          class="callsign-input account-overlay-input"
          maxlength="20"
          placeholder="ENTER CALL-SIGN"
          value="${escapeHtml(initialValue)}"
          autocomplete="off"
          spellcheck="false"
        />
      </div>

      <div id="callsign-status-msg" class="callsign-status-msg" aria-live="polite"></div>

      <div class="callsign-actions">
        <button type="button" class="start-btn start-btn-secondary" id="callsign-btn-cancel">
          <span class="start-btn-text">CANCEL</span>
        </button>
        <button type="button" class="start-btn start-btn-primary" id="callsign-btn-submit">
          <span class="start-btn-text">CONFIRM CALL-SIGN</span>
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(layer);

  const input = layer.querySelector('#callsign-input-field');
  const previewName = layer.querySelector('#callsign-preview-name');
  const charCount = layer.querySelector('#callsign-char-count');
  const statusMsg = layer.querySelector('#callsign-status-msg');
  const cancelBtn = layer.querySelector('#callsign-btn-cancel');
  const submitBtn = layer.querySelector('#callsign-btn-submit');
  const closeBtn = layer.querySelector('#callsign-close-btn');

  function updatePreview() {
    const val = (input?.value || '').trim();
    if (charCount && input) charCount.textContent = `${input.value.length} / 20`;
    if (previewName) {
      if (val.length > 0) {
        previewName.textContent = val;
        previewName.classList.remove('callsign-placeholder-name');
      } else {
        previewName.textContent = 'ENTER CALL-SIGN...';
        previewName.classList.add('callsign-placeholder-name');
      }
    }
    if (val.length >= 3) {
      input?.classList.remove('callsign-input-error');
      if (statusMsg && statusMsg.textContent === 'Call-Sign must have at least 3 characters.') {
        statusMsg.textContent = '';
      }
    }
  }

  input?.addEventListener('input', updatePreview);

  async function handleSave() {
    const raw = (input?.value || '').trim();
    if (raw.length < 3) {
      if (statusMsg) {
        statusMsg.style.color = '#f87171';
        statusMsg.textContent = 'Call-Sign must have at least 3 characters.';
      }
      input?.classList.add('callsign-input-error');
      audio?.playError?.();
      input?.focus();
      return;
    }

    if (submitBtn) submitBtn.disabled = true;
    const submitText = submitBtn?.querySelector('.start-btn-text');
    if (submitText) submitText.textContent = 'TRANSMITTING...';
    if (statusMsg) {
      statusMsg.style.color = '#00f0ff';
      statusMsg.textContent = 'Transmitting to Central Command...';
    }
    audio?.playUiClick?.();

    const res = await setDisplayName(raw);
    if (res?.ok) {
      if (statusMsg) {
        statusMsg.style.color = '#22c55e';
        statusMsg.textContent = `Call-Sign confirmed: ${res.displayName}!`;
      }
      if (submitText) submitText.textContent = 'CONFIRMED';
      setTimeout(() => {
        closeModal();
        if (onSaved) onSaved(res.displayName);
        if (res.isNewAccount && res.recoveryCode) {
          showRecoveryCode({ recoveryCode: res.recoveryCode });
        }
      }, 650);
    } else {
      if (submitBtn) submitBtn.disabled = false;
      if (submitText) submitText.textContent = 'CONFIRM CALL-SIGN';
      if (statusMsg) {
        statusMsg.style.color = '#f87171';
        statusMsg.textContent = res?.message || 'Could not save Call-Sign.';
      }
      input?.classList.add('callsign-input-error');
      audio?.playError?.();
    }
  }

  submitBtn?.addEventListener('click', handleSave);
  cancelBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    closeModal();
  });
  closeBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    closeModal();
  });

  input?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      audio?.playUiClick?.();
      closeModal();
    }
  });

  layer.addEventListener('click', (e) => {
    if (e.target === layer) {
      audio?.playUiClick?.();
      closeModal();
    }
  });

  const handleEsc = (e) => {
    if (e.key === 'Escape') {
      audio?.playUiClick?.();
      closeModal();
    }
  };
  window.addEventListener('keydown', handleEsc);

  setTimeout(() => {
    input?.focus();
    input?.select();
  }, 50);
}
